// src/components/product/TagSelector/TagSelector.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X, Tag, Plus } from 'lucide-react';
import { getProductTags, createProductTag } from '../../../services/api/productTags';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './TagSelector.module.css';

const TagSelector = ({ 
  selectedTagIds = [], 
  onTagsChange, 
  placeholder = "Select tags",
  error = null,
  allowCreateNew = true
}) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const [isOpen, setIsOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [createTagError, setCreateTagError] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const createInputRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setNewTagName('');
        setCreateTagError('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load tags when component mounts
  useEffect(() => {
    if (tags.length === 0) {
      loadTags();
    }
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current && !showCreateForm) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen, showCreateForm]);

  // Focus create input when create form shows
  useEffect(() => {
    if (showCreateForm && createInputRef.current) {
      setTimeout(() => {
        createInputRef.current.focus();
      }, 100);
    }
  }, [showCreateForm]);

  const loadTags = async () => {
    try {
      setLoading(true);
      const response = await getProductTags();
      setTags(response || []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tagId) => {
    const isSelected = selectedTagIds.includes(tagId);
    let newSelectedTags;
    
    if (isSelected) {
      newSelectedTags = selectedTagIds.filter(id => id !== tagId);
    } else {
      newSelectedTags = [...selectedTagIds, tagId];
    }
    
    onTagsChange(newSelectedTags);
  };

  const handleTagRemove = (tagId) => {
    const newSelectedTags = selectedTagIds.filter(id => id !== tagId);
    onTagsChange(newSelectedTags);
  };

  const handleSelectorClick = () => {
    setIsOpen(!isOpen);
  };

  const handleCreateNewClick = () => {
    setShowCreateForm(true);
    setSearchTerm('');
    setCreateTagError('');
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewTagName('');
    setCreateTagError('');
  };

  const validateTagName = (name) => {
    if (!name.trim()) {
      return t('productTags.nameRequired');
    }
    if (name.trim().length < 2) {
      return t('productTags.nameMinLength');
    }
    if (name.trim().length > 50) {
      return t('productTags.nameMaxLength');
    }
    
    // Check if tag already exists
    const existingTag = tags.find(tag => 
      tag.name.toLowerCase() === name.trim().toLowerCase()
    );
    if (existingTag) {
      return t('productTags.duplicateNameError');
    }
    
    return null;
  };

  const handleCreateTag = async () => {
    const validationError = validateTagName(newTagName);
    if (validationError) {
      setCreateTagError(validationError);
      return;
    }

    setIsCreating(true);
    setCreateTagError('');

    try {
      const tagData = {
        name: newTagName.trim()
      };

      const response = await createProductTag(tagData);
      
      // Add the new tag to the tags list
      const newTag = response.tag;
      setTags(prev => [...prev, newTag]);
      
      // Automatically select the new tag
      onTagsChange([...selectedTagIds, newTag.id]);
      
      // Reset form
      setNewTagName('');
      setShowCreateForm(false);
      
      // Show success message
      success(t('productTags.tagCreatedSuccess', { tagName: newTag.name }), {
        title: t('common.success'),
        duration: 3000
      });
      
    } catch (error) {
      console.error('Error creating tag:', error);
      if (error.response?.data?.message) {
        setCreateTagError(error.response.data.message);
      } else {
        setCreateTagError(t('productTags.failedToCreate'));
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateTag();
    } else if (e.key === 'Escape') {
      handleCancelCreate();
    }
  };

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedTags = tags.filter(tag => selectedTagIds.includes(tag.id));

  // Load tags if they haven't been loaded yet and we have selected tag IDs
  useEffect(() => {
    if (selectedTagIds.length > 0 && tags.length === 0) {
      loadTags();
    }
  }, [selectedTagIds, tags.length]);

  // Check if search term could be a new tag
  const canCreateFromSearch = allowCreateNew && 
    searchTerm.trim() && 
    !filteredTags.some(tag => tag.name.toLowerCase() === searchTerm.toLowerCase());

  return (
    <div className={styles.tagSelector} ref={dropdownRef}>
      <div 
        className={`${styles.selectorInput} ${error ? styles.error : ''} ${isOpen ? styles.open : ''}`}
        onClick={handleSelectorClick}
      >
        <div className={styles.selectedContent}>
          {selectedTags.length > 0 ? (
            <div className={styles.selectedTags}>
              {selectedTags.map((tag) => (
                <div key={tag.id} className={styles.selectedTag}>
                  <span className={styles.tagText}>{tag.name}</span>
                  <button
                    type="button"
                    className={styles.removeTagButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTagRemove(tag.id);
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <ChevronDown 
          size={18} 
          className={`${styles.chevron} ${isOpen ? styles.rotated : ''}`} 
        />
      </div>

      {error && <span className={styles.errorText}>{error}</span>}

      {isOpen && (
        <div className={styles.dropdown}>
          {!showCreateForm ? (
            <>
              {/* Search Input */}
              <div className={styles.searchContainer}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t('productTags.searchTags')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              {/* Create New Tag from Search */}
              {canCreateFromSearch && (
                <div className={styles.createFromSearch}>
                  <button
                    type="button"
                    className={styles.createFromSearchButton}
                    onClick={() => {
                      setNewTagName(searchTerm);
                      setShowCreateForm(true);
                    }}
                  >
                    <Plus size={16} />
                    <span>{t('productTags.createNewTag')}: "{searchTerm}"</span>
                  </button>
                </div>
              )}

              {/* Tags List */}
              <div className={styles.tagsList}>
                {loading ? (
                  <div className={styles.loadingState}>
                    <div className={styles.loadingSpinner}></div>
                    <span>{t('productTags.loadingTags')}</span>
                  </div>
                ) : filteredTags.length > 0 ? (
                  filteredTags.map((tag) => (
                    <TagItem
                      key={tag.id}
                      tag={tag}
                      isSelected={selectedTagIds.includes(tag.id)}
                      onToggle={() => handleTagToggle(tag.id)}
                    />
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    {searchTerm ? t('productTags.noSearchResults') : t('productTags.noTags')}
                  </div>
                )}
              </div>

              {/* Create New Tag Button */}
              {allowCreateNew && (
                <div className={styles.createNewSection}>
                  <button
                    type="button"
                    className={styles.createNewButton}
                    onClick={handleCreateNewClick}
                  >
                    <Plus size={16} />
                    {t('productTags.createNewTag')}
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Create Tag Form */
            <div className={styles.createTagForm}>
              <div className={styles.createFormHeader}>
                <h3>{t('productTags.createNewTag')}</h3>
                <button
                  type="button"
                  className={styles.createFormClose}
                  onClick={handleCancelCreate}
                >
                  <X size={16} />
                </button>
              </div>
              
              <div className={styles.createForm}>
                <div className={styles.createFormGroup}>
                  <label htmlFor="newTagName">{t('productTags.tagName')} *</label>
                  <input
                    ref={createInputRef}
                    type="text"
                    id="newTagName"
                    value={newTagName}
                    onChange={(e) => {
                      setNewTagName(e.target.value);
                      setCreateTagError('');
                    }}
                    onKeyDown={handleCreateInputKeyDown}
                    placeholder={t('productTags.tagNamePlaceholder')}
                    className={createTagError ? styles.errorInput : ''}
                    disabled={isCreating}
                    maxLength={50}
                  />
                  {createTagError && (
                    <span className={styles.createErrorText}>{createTagError}</span>
                  )}
                  <span className={styles.createHint}>{t('productTags.nameHint')}</span>
                </div>
                
                <div className={styles.createFormActions}>
                  <button
                    type="button"
                    className={styles.createCancelButton}
                    onClick={handleCancelCreate}
                    disabled={isCreating}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className={styles.createSubmitButton}
                    onClick={handleCreateTag}
                    disabled={isCreating || !newTagName.trim()}
                  >
                    {isCreating ? (
                      <>
                        <div className={styles.createLoadingSpinner}></div>
                        {t('productTags.savingTag')}
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        {t('productTags.createTag')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TagItem = ({ tag, isSelected, onToggle }) => {
  return (
    <div 
      className={`${styles.tagItem} ${isSelected ? styles.selected : ''}`}
      onClick={onToggle}
    >
      <div className={styles.tagContent}>
        <Tag size={16} className={styles.tagIcon} />
        <span className={styles.tagName}>{tag.name}</span>
      </div>
      
      <div className={styles.checkbox}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

export default TagSelector;